/**
 * useListingsMapState — Map/list state for ListingsClient.
 *
 * Extracts bounds management, infinite query, URL sync,
 * and interaction callbacks from the component.
 */

import { usePathname, useRouter, useSearchParams } from '@/shared/i18n/routing';
import { bff } from '@/shared/lib/bff';
import { formatBoundsForUrl, snapBoundsToGrid, type MapBounds } from '@/shared/lib/geo/bounds';
import {
  hasDiscreteLocation,
  toBffFilters,
  type ListingsFilterSet,
} from '@/shared/lib/listings/listings-filter-set';
import { queryKeys } from '@/shared/lib/query-keys';
import type { ListingCardData, MapPositionData } from '@/shared/lib/types/listing-card';
import type { SearchListingsResult } from '@/shared/lib/types/search';
import { bffError } from '@letsgethome/lib/server-errors';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { localeFromPath } from '@letsgethome/lib/weburl';
import {
  applyListingsFilterMutation,
  LISTINGS_BOUNDS_PARAM_NAMES,
  type ListingsBoundsEdge,
} from '@letsgethome/routes';
import type { ListingId } from '@letsgethome/types/branded';
import type { PageStanding } from '@letsgethome/types/paginated';
import { useDebouncedValue } from '@letsgethome/ui/hooks/use-debounced-value';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigation } from 'react-router';

import { highlightedPinId } from './listings-selection';
import type { ListingsResults } from './ListingsSheet';
import { RESTING_POSITION, resultsSheetReducer } from './results-sheet-state';

const MAP_DEBOUNCE_MS = 500;

interface UseListingsMapStateArgs {
  initialListings: ListingCardData[];
  initialMeta: {
    totalCount: number;
    page: number;
    perPage: number;
    hasMore: boolean;
    standing: PageStanding;
  };
  initialMapPosition?: MapPositionData;
  mode: TransactionModeValue;
  /** The URL's filter set — required; see `ListingsClientProps.filters`. */
  filters: ListingsFilterSet;
}

/**
 * The pane's state for a key the query HAS answered — `ready`, or the declared
 * `pastTheEnd` arm when the answer's first page stands past the search's last
 * one (#6922). Read off the answer's own `standing`, never guessed from an
 * empty card list: a filter that matches nothing is `ready` with no cards (the
 * "no listings" statement), a page past the end of a search that DOES match is
 * `pastTheEnd` — two states, two presentations.
 */
export function answeredResults(pages: readonly SearchListingsResult[]): ListingsResults {
  const first = pages[0];
  // An answered query with no page has no cards and no standing to read — it
  // is an empty `ready`, stated, rather than a `within` invented for it.
  if (first === undefined) return { kind: 'ready', listings: [], totalCount: 0 };
  const { standing, totalCount } = first;
  switch (standing.kind) {
    case 'pastTheEnd':
      return { kind: 'pastTheEnd', totalCount, lastPage: standing.lastPage };
    case 'within':
      return { kind: 'ready', listings: pages.flatMap((p) => p.listings), totalCount };
    default:
      return assertNever(standing);
  }
}

export function useListingsMapState({
  initialListings,
  initialMeta,
  initialMapPosition,
  mode,
  filters,
}: UseListingsMapStateArgs) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const locale = localeFromPath(pathname);

  const [mapPosition] = useState<MapPositionData | undefined>(initialMapPosition);

  /**
   * The two SEPARATE facts the canvas holds about what the reader is looking
   * at (`listings-selection.ts`).
   *
   * They were one `ListingSelection` union with an `origin`, which forced an
   * arbitration — `hoverSelection` — that a card highlight WON: it replaced an
   * open marker preview, closing the popover and stranding the drawer's stored
   * hiddenness behind it. Nothing arbitrates now, because nothing collides:
   * a highlight is a highlight and a preview is a preview.
   *
   * (The list's own ring and its scroll-into-view were selectors over that
   * union too, until obs. 4: both existed only to report which card the FIRST
   * click had selected, and the first click now opens the listing.)
   */
  const [highlightedCardId, setHighlightedCardId] = useState<ListingId | null>(null);
  const [markerPreview, setMarkerPreview] = useState<ListingCardData | null>(null);

  // `userInteracted` flips true only on a deliberate map gesture (drag/zoom) —
  // NOT the initial camera settle on mount. Until then the SSR `initialData`
  // stands: no refetch, and no `?north=…` written to the URL on first land.
  // (The map's `onCameraChanged` fires on mount, which previously read as a user
  // pan → a failing bounds query + a spurious URL change immediately on load.)
  const [userInteracted, setUserInteracted] = useState(false);

  /**
   * The mobile results drawer's RAIL (obs. 5, `results-sheet-state.ts`).
   *
   * It is owned HERE, beside the map's own gestures, because a deliberate
   * pan/zoom is what collapses the drawer out of the way. What it no longer
   * holds is whether the drawer is on SCREEN: that is derived from
   * `markerPreview` below, in the same render that decides whether the preview
   * card is painted.
   *
   * ★ AND THERE IS NO VIEWPORT EFFECT. There used to be one, dispatching a
   * `viewportLeftMobile` reset when the canvas crossed to `md+`, because the
   * state could be HIDDEN and a hidden drawer re-mounting at a narrower width
   * had nothing to re-open it. It fired only on LEAVING mobile, which is
   * exactly the edge that could not repair the stranding. Every value
   * `SheetPosition` can hold is a rail a reader can see and grab, so a rail
   * that survives a trip to the desktop pane is the reader's own last choice
   * on this very page — not a state to be rescued from.
   */
  const [sheetPosition, dispatchSheet] = useReducer(resultsSheetReducer, RESTING_POSITION);

  const [rawBounds, setRawBounds] = useState<MapBounds | undefined>(undefined);
  const debouncedBounds = useDebouncedValue(rawBounds, MAP_DEBOUNCE_MS);
  const snappedBounds =
    userInteracted && debouncedBounds ? snapBoundsToGrid(debouncedBounds) : undefined;

  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  /**
   * THE PANE'S ONE QUERY, for the key the page is showing and nothing else.
   *
   * The key is the URL's whole fetch identity — mode, the gesture's bounds,
   * and the loader's `filters` (which carries `page`).
   *
   * ★ THE LOADER'S ANSWER IS WRITTEN INTO THE CACHE for exactly the key it
   * answered, every time it changes — not offered as `initialData` (#7816).
   * `initialData` is read only when a query is CREATED, so a filter key the
   * cache already held (studio → 3 → studio, inside `gcTime`) painted its OLD
   * entry, ignored the loader's fresh answer, and — once that entry was past
   * `staleTime` — fetched the same page a second time. The write happens in
   * render, BEFORE the query reads its key, so the first paint is the loader's
   * answer and the entry is fresh: nothing to fetch. It is the same move
   * TanStack's own `HydrationBoundary` makes for a query nothing observes yet,
   * and nothing observes this key: the hook's observer is still on the key it
   * rendered last. It runs only while no map gesture has put bounds in the key
   * — the loader never answers a bounds key.
   *
   * There is no `enabled` switch. It was `!!snappedBounds`, so no key the pane
   * held before a map gesture was ever FETCHED. A map gesture is now the same
   * mechanism as everything else: bounds enter the key, nothing seeds it, it is
   * fetched.
   */
  const queryClient = useQueryClient();
  const queryKey = queryKeys.listings.map(mode, snappedBounds, filters);
  const [seededAnswer, setSeededAnswer] = useState<ListingCardData[] | null>(null);
  if (seededAnswer !== initialListings) {
    // React's "adjust state while rendering" form: the answer this render was
    // handed differs from the one last written, so write it — once per answer.
    setSeededAnswer(initialListings);
    if (!snappedBounds) {
      queryClient.setQueryData<InfiniteData<SearchListingsResult, number>>(queryKey, {
        pages: [
          {
            listings: initialListings,
            totalCount: initialMeta.totalCount,
            page: initialMeta.page,
            perPage: initialMeta.perPage,
            hasMore: initialMeta.hasMore,
            standing: initialMeta.standing,
            mapPosition: initialMapPosition,
          },
        ],
        pageParams: [initialMeta.page],
      });
    }
  }

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const result = await bff('/api/search/listings', {
        mode,
        page: pageParam,
        // The URL's filters ALWAYS ride along; the viewport is folded in only
        // when the wire's bounds-XOR-discrete-location rule allows it, so a
        // pan on a district- or estate-scoped page refines nothing rather than
        // building a request the backend rejects. Previously this sent
        // `undefined` (the prop was never passed) and every pan silently
        // widened the search to the whole viewport.
        filters: toBffFilters(filters, debouncedBounds),
        locale,
      });
      if (!result.success) {
        throw bffError(result.error, 'Search failed');
      }
      return result.data!;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    // While a key with no entry loads (a gesture's bounds), the previous key's
    // result stays on screen under the refreshing cue below.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const { fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = query;

  /**
   * What the pane holds, as the TYPED STATE it is — never a prop standing in
   * for data the query does not have. The pane renders each arm
   * (`ListingsResults`); "an error before any data" is an error, not an empty
   * list reporting 0 results.
   */
  const results: ListingsResults =
    query.status === 'pending'
      ? { kind: 'loading' }
      : query.status === 'error'
        ? { kind: 'error', message: query.error.message }
        : answeredResults(query.data.pages);

  /**
   * The REFRESHING cue: the pane is showing a list that is about to be
   * replaced. A query fetch is one cause (a gesture's key, a stale key); the
   * OTHER is the router itself — a filter Apply is a search-only navigation of
   * THIS page, and for its ~0.5 s loader round trip the pane still shows the
   * previous filter set's list while the URL and the badge already name the
   * new one. Scoped to this page's own path, so a navigation AWAY does not
   * dress the outgoing pane as refreshing.
   */
  const navigation = useNavigation();
  const navigatingThisPage =
    navigation.state === 'loading' && navigation.location.pathname === pathname;
  const isRefreshing = (isFetching && !isFetchingNextPage) || navigatingThisPage;

  // The pin the map styles as selected: the open preview's while there is one,
  // otherwise the card the reader is pointing at or has tabbed to. The
  // precedence is load-bearing — `ListingsMap` resolves the popover's reference
  // element by this id — and the argument is in `highlightedPinId`.
  const selectedId = highlightedPinId(markerPreview, highlightedCardId);

  const updateUrlWithBounds = useCallback(
    (bounds: MapBounds) => {
      const current = searchParamsRef.current;
      const formatted = formatBoundsForUrl(bounds);
      // Idempotency guard: only navigate when a bound value actually changes.
      // If the URL already encodes these snapped bounds the `replace` is skipped,
      // so the effect physically cannot loop even if `router` ever regains an
      // unstable identity — defence-in-depth behind the memoized useRouter seam.
      const changed = (Object.keys(formatted) as ListingsBoundsEdge[]).some(
        (edge) => current.get(LISTINGS_BOUNDS_PARAM_NAMES[edge]) !== formatted[edge]
      );
      if (!changed) return;
      // A new viewport is a new result set, so it goes through the codec's ONE
      // filter-mutation seam, which drops `page` (#8721). This used to copy the
      // whole query string and set the four edges onto it, so a pan on
      // `?page=2` asked for page 2 of a search the reader had never seen page 1
      // of — the upstream way readers reached #6922's past-the-end canvas.
      const params = applyListingsFilterMutation(current, { bounds: formatted });
      // Same-page query update (map pan/zoom → bounds), not a route navigation.
      void router.setQuery(params, { replace: true, scroll: false });
    },
    [router]
  );

  // Bounds are written to the URL only when they actually filter something.
  // On a district/estate-scoped page the viewport loses the XOR (see
  // `resolveViewport`), so writing it would produce a shareable URL that the
  // SSR loader must then reject as `bounds` + `district_slug` — a link that
  // 400s on reload despite working in the tab that created it.
  const viewportIsMeaningful = !hasDiscreteLocation(filters);
  useEffect(() => {
    if (userInteracted && debouncedBounds && viewportIsMeaningful) {
      updateUrlWithBounds(debouncedBounds);
    }
  }, [userInteracted, debouncedBounds, viewportIsMeaningful, updateUrlWithBounds]);

  /**
   * A pin tap raises the preview card over the map's bottom edge, which is the
   * ONE moment the results drawer is allowed to leave the screen.
   *
   * ★ THERE IS NOTHING TO DISPATCH HERE ANY MORE, and that is the fix. This
   * used to send `previewOpened` into the drawer's reducer — on every viewport,
   * including the desktop one where the drawer is unmounted, which is how a
   * state machine ended up hidden for a view that did not exist. The drawer's
   * openness is now READ off `markerPreview` in the same render that decides
   * whether the card below is painted (`resultsSheetIsOpen`), so setting the
   * preview IS opening it and clearing the preview IS the way back.
   * (`Z_LAYERS.mapPreview` sits BELOW `resultsSheet` and stays there: the pair
   * is resolved by state, not by z-order. See the z-layers row.)
   */
  const handleMarkerClick = useCallback((listing: ListingCardData) => {
    setMarkerPreview(listing);
  }, []);

  const handleDismissPreview = useCallback(() => {
    setMarkerPreview(null);
  }, []);

  /**
   * The map-sync affordance: the pointer entering a card — or the keyboard
   * tabbing into it — highlights that listing's pin, and leaving clears the
   * highlight. This REPLACES `handleCardClick`, which selected on the first
   * click and only navigated on the second: a card is a link, and the link now
   * owns every click it gets (obs. 4).
   *
   * It is a plain setter because it CANNOT touch the preview any more. The
   * function it replaces (`hoverSelection`) existed only to decide what a
   * highlight did to a marker selection sharing its slot, and the answer it
   * gave — replace it — closed the popover and stranded the drawer.
   */
  const handleCardHighlight = useCallback((id: ListingId | null) => {
    setHighlightedCardId(id);
  }, []);

  // A real map gesture (drag/zoom). From here on, camera moves drive the
  // viewport search + the URL; the initial mount settle does not. The second
  // half — get the drawer out of the map's way — is a TYPED EVENT now (SV-C4
  // used a `document` CustomEvent bus, then a monotonic counter; both were
  // "something happened" with no content). `mapGesture` says what happened,
  // is idempotent at `peek`, and cannot resurrect a sheet the preview hid.
  const handleInteractionStart = useCallback(() => {
    setUserInteracted(true);
    dispatchSheet({ type: 'mapGesture' });
  }, []);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setRawBounds(bounds);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      void fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  return {
    mapPosition,
    selectedId,
    /**
     * The open marker preview, or `null`. It is the popover's source on
     * desktop, the preview card's on mobile, AND — through
     * `resultsSheetIsOpen`, at the one place that renders the drawer — the
     * reason the drawer is off screen. One value, three readers, no copy.
     */
    markerPreview,
    results,
    isRefreshing,
    isFetchingNextPage,
    hasNextPage,
    handleMarkerClick,
    handleDismissPreview,
    handleCardHighlight,
    handleInteractionStart,
    handleBoundsChange,
    handleLoadMore,
    sheetPosition,
    /**
     * The sheet's own events (a drag settling, the "Map" button). It is the
     * reducer's `dispatch` — a caller can only send something the event union
     * has an arm for, which is the whole point of routing them through here.
     */
    onSheetEvent: dispatchSheet,
  };
}
